import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { appsAdvApiRouterGetRsvpStatus } from '@/lib/api';

interface RsvpStatus {
  is_rsvped: boolean;
  is_waitlisted: boolean;
  waitlist_position: number | null;
  rsvp: any;
  loading: boolean;
  error: string | null;
  hasRated: boolean;
  rating: boolean | null; // true for thumbs up, false for thumbs down, null for no rating
  refresh: () => void;
}

export const useRsvpStatus = (rideId: string | undefined): RsvpStatus => {
  const [status, setStatus] = useState<Omit<RsvpStatus, 'refresh'>>({
    is_rsvped: false,
    is_waitlisted: false,
    waitlist_position: null,
    rsvp: null,
    loading: false,
    error: null,
    hasRated: false,
    rating: null
  });
  
  const { user } = useAuth();

  const checkRsvpStatus = async () => {
    if (!rideId || !user) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }));
      const response = await appsAdvApiRouterGetRsvpStatus(rideId);
      setStatus({
        is_rsvped: response.data.is_rsvped,
        is_waitlisted: response.data.is_waitlisted ?? false,
        waitlist_position: response.data.waitlist_position ?? null,
        rsvp: response.data.rsvp,
        loading: false,
        error: null,
        hasRated: response.data.rsvp?.rating !== null && response.data.rsvp?.rating !== undefined,
        rating: response.data.rsvp?.rating || null
      });
    } catch (error: any) {
      setStatus({
        is_rsvped: false,
        is_waitlisted: false,
        waitlist_position: null,
        rsvp: null,
        loading: false,
        error: error.message || 'Failed to check RSVP status',
        hasRated: false,
        rating: null
      });
    }
  };

  useEffect(() => {
    checkRsvpStatus();
  }, [rideId, user]);

  return {
    ...status,
    refresh: checkRsvpStatus
  };
}; 